/**
 * Defines the central, global state management for authentication and user profile data.
 * @see {@link https://github.com/preactjs/signals/tree/main/packages/utils} - Uses `@preact-signals/utils`'s `createFlatStore` for state creation.
 * @see {@link https://preactjs.com/guide/v10/signals/#effect} - Uses `effect` to reactively subscribe to Firestore documents based on auth state.
 *
 * This file is the single source of truth for the application's authentication and user profile state.
 * It demonstrates a powerful, purely signal-based approach to managing dependent asynchronous data.
 *
 * The key pattern here is the use of `effect` to create a reactive data pipeline:
 * 1.  `onAuthStateChanged` (from Firebase SDK) updates the `SessionState.user` signal.
 * 2.  An `effect` is set up to "watch" the `SessionState.user` signal (specifically, the `SessionState.profileRef` computed signal which depends on `user`).
 * 3.  When `SessionState.user` changes (e.g., a user logs in), the `effect` automatically runs. It subscribes to the new user's profile document in Firestore.
 * 4.  The `onSnapshot` listener from Firestore then updates the `SessionState.profile` signal with the profile data.
 * 5.  When the user logs out, `SessionState.user` becomes `undefined`. The `effect`'s cleanup function is automatically triggered, which unsubscribes from the Firestore listener, preventing memory leaks.
 *
 * This creates a declarative, self-managing system for auth-dependent data, completely free of traditional React lifecycle hooks.
 */

import type { ParsedToken, User } from 'firebase/auth';

import type { AdminRole, AdminRoleClaims } from '@elara/domain/roles';

import type { AuthContainer } from '#containers/FirebaseAuthContainer.js';
import type { SessionCookiePort } from '#lib/session-cookie-port.js';
import type { SocialAuthPort, SocialProvider } from '#lib/social-auth-port.js';

import { ADMIN_ROLE_NAMES } from '@elara/domain/roles';

import { getAnalyticsReporter } from '#lib/analytics.js';
import { trackLogin, trackSignUp } from '#lib/analytics-events.js';
import { getErrorReporter } from '#lib/error-reporting.js';
import { createState } from '#lib/signals-utils.js';

/** Custom claims interface for privileged access */
export interface CustomClaims extends ParsedToken {
  admin?: boolean;
  dev?: boolean;
  booking?: boolean;
  chat_viewer?: boolean;
  marketing?: boolean;
}

/**
 * The main reactive store for all authentication and user profile state.
 *
 * @remark All the getters in FlatStore are transformed into computed signals, the values are signals.
 * The functions are not touched, they are the actions on the state.
 */
/** Optional callback to warm up Cloud Functions when user logs in */
export type WarmupCallback = () => Promise<unknown>;

export function createSessionState(
  auth: AuthContainer,
  sessionCookie: SessionCookiePort,
  onWarmup?: WarmupCallback,
  onDebugWarmup?: WarmupCallback,
  /**
   * Platform social sign-in port (ELARA-728). Web wraps `signInWithPopup`; React Native runs the
   * native Google / Apple flow and exchanges the result for a Firebase credential. Optional: when
   * absent (e.g. a unit test that doesn't exercise social auth), `signInWithGoogle` / `signInWithApple`
   * throw a clear "not configured" error rather than silently no-op.
   */
  socialAuth?: SocialAuthPort,
) {
  const state = createState({
    /**
     * The raw Firebase User object, or `undefined` if not authenticated.
     * This is the root signal that drives all other auth-related state.
     */
    user: undefined as User | undefined,
    /**
     * Returns `true` while the *initial* auth state is still being determined (and during sign-out,
     * where the shell is torn down anyway).
     *
     * **This is not an in-flight flag for individual auth operations.** Consumers treat it as
     * "the session is not yet known" and render a placeholder instead of the real screen — web's
     * `App` shows a loading screen, and mobile's `RootNavigator` mounts *only* the splash, unmounting
     * the entire signed-out stack. So toggling it around a sign-in/sign-up attempt destroys the auth
     * stack's navigation history: a failed sign-up dropped the user back on `Login` (with the signup
     * error banner) instead of leaving them on `Signup` where the error belongs. Per-form pending
     * state belongs to `AuthFormState.isLoading`, which drives the button spinners on both platforms.
     */
    isLoading: true, // Start as true to prevent auth flickering
    /**
     * Custom claims from the user's ID token, containing privilege flags (admin, dev).
     * Refreshed on auth state change and can be manually refreshed via `refreshCustomClaims()`.
     */
    customClaims: undefined as CustomClaims | undefined,
    /** A computed signal that returns the UID of the authenticated user, or `undefined`. */
    get userId(): string | undefined {
      return this.user?.uid;
    },
    /** A computed signal that returns `true` if a user is currently authenticated. */
    get isAuthenticated(): boolean {
      return !!this.user;
    },
    /**
     * A computed signal that returns `true` if the current user has privileged access.
     * Privileged users can view other users' trips for debugging purposes.
     *
     * Privilege is granted if ANY of these conditions are met:
     * - `customClaims.admin === true`
     * - `customClaims.dev === true`
     * - User's email ends with `@elara.travel` AND email is verified
     */
    get isPrivileged(): boolean {
      // Check custom claims first (admin or dev role)
      if (this.customClaims?.admin === true || this.customClaims?.dev === true) {
        return true;
      }
      // Fallback to email domain check (verified @elara.travel email)
      return !!(this.user?.emailVerified && this.user?.email?.endsWith('@elara.travel'));
    },
    hasAdminRole(role: AdminRole): boolean {
      return this.customClaims?.[role] === true;
    },
    get adminRoleClaims(): AdminRoleClaims {
      return {
        booking: this.hasAdminRole('booking'),
        chat_viewer: this.hasAdminRole('chat_viewer'),
        marketing: this.hasAdminRole('marketing'),
      };
    },
    get canAccessAdmin(): boolean {
      return this.isPrivileged || ADMIN_ROLE_NAMES.some((role) => this.hasAdminRole(role));
    },
    /**  Logs out the current user */
    async logout(): Promise<void> {
      this.isLoading = true;
      await auth
        .signOut()
        .then(() => {
          console.log('[SessionState] Logout successful');
          this.user = undefined;
          this.customClaims = undefined;
          sessionCookie.setSessionToken(undefined);
        })
        .catch((error: unknown) => {
          this.latestError = error as Error;
          console.error('[SessionState] Logout failed', { error });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    latestError: undefined as Error | undefined,
    /**
     * Fire-and-forget warmup to pre-heat the Cloud Run server. Non-fatal: a
     * cold server simply means the first request pays the cold-start cost.
     * Extracted so both initial auth and {@link handleResume} share one path.
     */
    fireWarmup(): void {
      if (!onWarmup) return;
      onWarmup()
        .then((result) => console.debug('[SessionState] Cloud Functions warmup complete', result))
        .catch((err) => console.warn('[SessionState] Cloud Functions warmup failed (non-fatal)', err));
    },
    /**
     * Re-warms auth + backend after the app returns from background (ELARA-695).
     *
     * Mobile OSes suspend timers/sockets while backgrounded, so the periodic
     * id-token refresh (`onIdTokenChanged`) and the initial warmup never re-fire
     * on cold resume — leaving a stale `__session` cookie and a cold server.
     * The lifecycle monitor calls this on resume. Idempotent and a no-op when
     * signed out.
     */
    async handleResume(): Promise<void> {
      if (!this.user) {
        console.debug('[SessionState] Skipping resume re-warmup (not signed in)');
        return;
      }
      console.debug('[SessionState] Handling resume — refreshing id token + warming backend');
      // Force an id-token refresh so the __session cookie and custom claims are
      // fresh after a long suspend (also re-fires the debug warmup internally).
      await this.refreshCustomClaims(true);
      // Re-fire the main warmup to wake a server that may have scaled to zero.
      this.fireWarmup();
    },
    /**
     * Refreshes the custom claims from the user's ID token.
     * Call this after auth state changes or when you expect claims to have been updated.
     *
     * @param forceRefresh - If true, forces a token refresh from the server
     */
    async refreshCustomClaims(forceRefresh?: boolean): Promise<void> {
      try {
        const tokenResult = await auth.getIdTokenResult(forceRefresh);
        if (tokenResult) {
          this.customClaims = tokenResult.claims as CustomClaims;
          sessionCookie.setSessionToken(tokenResult.token);
          // Tag internal/staff sessions so reports can exclude them (claims are now known). Two
          // mechanisms: the `is_privileged` user property (flexible report-time / BigQuery filtering)
          // and `traffic_type=internal` (GA4's native Internal Traffic data filter; also covers
          // automatically-collected events). Dev/preview builds force `traffic_type` on at bootstrap.
          getAnalyticsReporter().setUserProperties({ is_privileged: String(this.isPrivileged) });
          getAnalyticsReporter().setInternalTraffic(this.isPrivileged);
          console.debug('[SessionState] Custom claims refreshed', {
            admin: this.customClaims?.admin,
            dev: this.customClaims?.dev,
            booking: this.customClaims?.booking,
            chat_viewer: this.customClaims?.chat_viewer,
          });

          // Fire-and-forget warmup call to pre-heat debug Cloud Functions for privileged users
          if (this.isPrivileged && onDebugWarmup) {
            onDebugWarmup()
              .then((result) => console.debug('[SessionState] Debug Cloud Functions warmup complete', result))
              .catch((err) => console.warn('[SessionState] Debug Cloud Functions warmup failed (non-fatal)', err));
          }
        } else {
          this.customClaims = undefined;
          sessionCookie.setSessionToken(undefined);
        }
      } catch (error) {
        console.error('[SessionState] Failed to refresh custom claims', { error });
        this.customClaims = undefined;
        sessionCookie.setSessionToken(undefined);
      }
    },
    /** Whether Sign in with Apple is available on this platform (iOS native only). */
    get isAppleSignInAvailable(): boolean {
      return socialAuth?.isAppleAvailable ?? false;
    },
    /** Initiates native/popup Google sign-in via the platform {@link SocialAuthPort}. */
    async signInWithGoogle(): Promise<boolean> {
      return this.signInWithSocialProvider('google');
    },
    /** Initiates native Apple sign-in (iOS) via the platform {@link SocialAuthPort}. */
    async signInWithApple(): Promise<boolean> {
      return this.signInWithSocialProvider('apple');
    },
    /**
     * Shared driver for the federated sign-in providers. Delegates the platform-specific OAuth dance to
     * the injected {@link SocialAuthPort}; a successful exchange flips the auth state through the same
     * `onAuthStateChanged` cascade as email sign-in. A user-cancelled flow resolves quietly (no
     * `latestError`) so the UI doesn't surface a "failure" the user deliberately chose.
     *
     * Returns whether a credential was actually obtained. Cancellation and success both resolve, and
     * callers that chain work onto a new session (signup creating the profile) need to tell them
     * apart — treating a cancelled popup as a sign-in produces an unauthenticated flow call.
     */
    async signInWithSocialProvider(provider: SocialProvider): Promise<boolean> {
      if (!socialAuth) {
        throw new Error('[SessionState] Social sign-in is not configured for this platform');
      }
      try {
        console.info('[SessionState] Initiating social sign-in', { provider });
        this.latestError = undefined;
        const outcome =
          provider === 'apple' ? await socialAuth.signInWithApple() : await socialAuth.signInWithGoogle();
        if (outcome.status === 'cancelled') {
          console.info('[SessionState] Social sign-in cancelled by user', { provider });
          return false;
        }
        // Any successful federated sign-in counts as a login (GA4 recommended event). The native
        // adapter rejects first-time provider identities because mobile signup is not supported, so
        // only an existing account can reach this point there. Web retains its own signup path.
        trackLogin(provider);
        console.info('[SessionState] Social sign-in successful', { provider, uid: outcome.credential.user.uid });
        return true;
      } catch (error) {
        console.error('[SessionState] Social sign-in failed', { provider, error });
        this.latestError = error as Error;
        throw error;
      }
    },
    async signInWithEmail(email: string, password: string): Promise<void> {
      try {
        console.info('[SessionState] Initiating email sign-in', { email });
        this.latestError = undefined;
        const result = await auth.signInWithEmailAndPassword(email, password);
        trackLogin('password');
        console.info('[SessionState] Email sign-in successful', { uid: result.user.uid });
      } catch (error) {
        console.error('[SessionState] Email sign-in failed', { error, email });
        this.latestError = error as Error;
        throw error;
      }
    },
    async signUpWithEmail(email: string, password: string, displayName?: string): Promise<void> {
      try {
        console.debug('[SessionState] Creating new user account', { email, displayName });
        this.latestError = undefined;
        const result = await auth.createUserWithEmailAndPassword(email, password);
        trackSignUp('password');
        console.debug('[SessionState] Account creation successful', { uid: result.user.uid });
        if (displayName) {
          await auth.updateProfile({ displayName });
          console.debug('[SessionState] Display name set on Firebase Auth user', { displayName });
        }
      } catch (error) {
        console.error('[SessionState] Account creation failed', { error, email });
        this.latestError = error as Error;
        throw error;
      }
    },
    /**
     * Sends a password reset email to the given address.
     * Use actionCodeSettings to set the URL where the user lands after clicking the link (e.g. app origin + /reset-password).
     */
    async sendPasswordResetEmail(
      email: string,
      actionCodeSettings?: { url: string; handleCodeInApp: boolean },
    ): Promise<void> {
      try {
        this.latestError = undefined;
        await auth.sendPasswordResetEmail(email, actionCodeSettings);
        console.info('[SessionState] Password reset email sent', { email });
      } catch (error) {
        console.error('[SessionState] sendPasswordResetEmail failed', { error, email });
        this.latestError = error as Error;
        throw error;
      }
    },
    /**
     * Completes the password reset flow using the code from the reset email and the new password.
     * Used on the reset-password page when the user is not signed in.
     */
    async confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
      try {
        this.latestError = undefined;
        await auth.confirmPasswordReset(oobCode, newPassword);
        console.info('[SessionState] Password reset confirmed');
      } catch (error) {
        console.error('[SessionState] confirmPasswordReset failed', { error });
        this.latestError = error as Error;
        throw error;
      }
    },
    /**
     * Applies an out-of-band action code (used for email verification and email recovery).
     */
    async applyActionCode(oobCode: string): Promise<void> {
      try {
        this.latestError = undefined;
        await auth.applyActionCode(oobCode);
        console.info('[SessionState] Action code applied');
      } catch (unknownError) {
        console.error('[SessionState] applyActionCode failed', { error: unknownError });
        const error = unknownError as Error;
        this.latestError = error;
        throw error;
      }
    },
    /**
     * Validates a password reset code and returns the email address associated with it.
     * Useful for custom password reset screens so we can show the user which account they are resetting.
     */
    async verifyPasswordResetCode(oobCode: string): Promise<string> {
      try {
        this.latestError = undefined;
        const email = await auth.verifyPasswordResetCode(oobCode);
        console.info('[SessionState] Password reset code verified');
        return email;
      } catch (error) {
        console.error('[SessionState] verifyPasswordResetCode failed', { error });
        this.latestError = error as Error;
        throw error;
      }
    },
    /**
     * Updates the current user's password.
     * If Firebase returns requires-recent-login, call reauthenticateWithEmailAndPassword then retry.
     */
    async updatePassword(newPassword: string): Promise<void> {
      try {
        this.latestError = undefined;
        await auth.updatePassword(newPassword);
        console.info('[SessionState] Password updated');
      } catch (error) {
        console.error('[SessionState] updatePassword failed', { error });
        this.latestError = error as Error;
        throw error;
      }
    },
    /**
     * Re-authenticates the current user with email and password.
     * Call before updatePassword when Firebase returns auth/requires-recent-login.
     */
    async reauthenticateWithEmailAndPassword(email: string, password: string): Promise<void> {
      try {
        this.latestError = undefined;
        const credential = auth.EmailAuthProvider.credential(email, password);
        await auth.reauthenticateWithCredential(credential);
        console.info('[SessionState] Re-authentication successful');
      } catch (error) {
        console.error('[SessionState] reauthenticateWithCredential failed', { error });
        this.latestError = error as Error;
        throw error;
      }
    },
    /**
     * This is the primary auth listener. It runs once at client startup and is managed by the Firebase SDK.
     * It updates the `SessionState.user` signal whenever the user's authentication state changes.
     */
    startMonitors(): () => void {
      let isFirstCheck = true;
      const unsubAuthState = auth.onAuthStateChanged((user) => {
        this.user = user ?? undefined;

        // Associate analytics with the pseudonymous Firebase UID (never PII). Cleared on sign-out.
        getAnalyticsReporter().setUserId(user?.uid);

        // Same UID onto crash reports, for both platforms in one place. Fatal classes like an iOS
        // watchdog termination arrive with no stack trace and no breadcrumbs, so without this the
        // account is unknowable — triage on ELARA-MOBILE-G could not tell who was affected or what
        // they had open. Still pseudonymous: the id only, never name/email/phone.
        getErrorReporter().setUser(user ? { id: user.uid } : undefined);

        // Refresh custom claims when user changes
        if (user) {
          void this.refreshCustomClaims();

          // Fire-and-forget warmup call to pre-heat Cloud Functions
          this.fireWarmup();
        } else {
          this.customClaims = undefined;
        }

        // Set loading to false after the first auth state check
        if (isFirstCheck) {
          this.isLoading = false;
          isFirstCheck = false;
        }
      });
      const unsubIdToken = auth.onIdTokenChanged(async (user) => {
        if (!user) {
          sessionCookie.setSessionToken(undefined);
          return;
        }

        try {
          sessionCookie.setSessionToken(await user.getIdToken());
        } catch (error) {
          console.warn('[SessionState] Failed to sync session auth cookie', { error });
          sessionCookie.setSessionToken(undefined);
        }
      });
      this.stopMonitors = () => {
        unsubAuthState?.();
        unsubIdToken?.();
        this.stopMonitors = undefined;
      };
      return this.stopMonitors;
    },
    stopMonitors: undefined as (() => void) | undefined,
    /**
     * Resets the session state to the initial state.
     */
    reset(): void {
      this.user = undefined;
      this.customClaims = undefined;
      this.latestError = undefined;
      sessionCookie.setSessionToken(undefined);
      this.isLoading = true; // Reset to initial loading state
    },
  });
  return state;
}

export type SessionState = ReturnType<typeof createSessionState>;
